import React from 'react';
import { Space, Divider, Table, Tag, Tooltip, Typography } from 'antd';
import PnL from '../../../components/PnL';
import NivoBarChart from '../../../charts/nivo/NivoBarChart';
import { prepareCalendarReturns } from "../utils";
import { RankColumn, NameColumn } from "../tableUtils";
import { stringMixin } from "../../../utils/strings";
import { Info } from "lucide-react";

const AnnualizedReturn = ({ cumulativeReturn, years }) => {
  const annualizedReturn = (Math.pow(1 + cumulativeReturn / 100, 1 / years) - 1) * 100; // Convert to annualized %

  const returnTagColor = annualizedReturn >= 0 ? 'bg-gradient-to-br from-emerald-100 to-cyan-50 text-emerald-900' : 'bg-gradient-to-br from-rose-100 to-red-50 text-rose-900';

  return (
    <Space size="small">
      <div style={{ width: 100 }}>
        <PnL precision={2} value={cumulativeReturn} />
      </div>
      <Tag className={`text-xs rounded-xl px-2 py-0.5 ${returnTagColor}`} bordered={false}>
        {stringMixin.pct(annualizedReturn, { prec: 2 })}
      </Tag>
    </Space>
  );
};

const PerformanceHistory = ({ items, itemColors }) => {
  const { data, keys, customColors } = prepareCalendarReturns([...items], itemColors);

  const columns = [
    NameColumn(itemColors),
    {
      title: 'YTD',
      dataIndex: 'ytd',
      key: 'ytd',
      render: (v, r) => <PnL value={r?.total?.ytd} />,
      sorter: (a, b) => a?.total?.ytd - b?.total?.ytd,
    },
    {
      title: '1 Year Change (%)',
      dataIndex: 'oneYear',
      key: 'oneYear',
      render: (v, r) => <PnL value={r?.['252']?.pct_r} precision={2} />,
      sorter: (a, b) => a?.['252']?.pct_r - b?.['252']?.pct_r,
    },
    {
      title: (
        <Space>
          <span>3 Yr Change</span>
          <Tooltip title={'Total Return % / Annualized Return %'}>
            <Info size={14} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'threeYear',
      key: 'threeYear',
      render: (v, r) => (
        <AnnualizedReturn cumulativeReturn={r?.['756']?.pct_r} years={3} />
      ),
      sorter: (a, b) => {
        const aAnnualized = Math.pow(1 + a?.['756']?.pct_r / 100, 1 / 3) - 1;
        const bAnnualized = Math.pow(1 + b?.['756']?.pct_r / 100, 1 / 3) - 1;
        return aAnnualized - bAnnualized;
      },
    },
    {
      title: '5 Year Change',
      dataIndex: 'fiveYear',
      key: 'fiveYear',
      render: (v, r) => (
        <AnnualizedReturn cumulativeReturn={r?.['1260']?.pct_r} years={5} />
      ),
      sorter: (a, b) => {
        const aAnnualized = Math.pow(1 + a?.['1260']?.pct_r / 100, 1 / 5) - 1;
        const bAnnualized = Math.pow(1 + b?.['1260']?.pct_r / 100, 1 / 5) - 1;
        return aAnnualized - bAnnualized;
      },
    },
  ];

  return (
    <>
      <div className="round-2 p-1">
        <Table columns={columns} dataSource={items} pagination={{ pageSize: 10 }} />
      </div>
      <Divider />
      <div className="round-2" style={{ height: 400 }}>
        <NivoBarChart
          data={data}
          keys={keys}
          customColors={customColors}
          legendSize="small"
          barPadding={0.4}
          borderRadius={4}
          useBarShadow={false}
        />
      </div>
    </>
  );
};

export default PerformanceHistory;
